import * as yup from 'yup'
import React, { useState, ReactNode, FC, useMemo } from 'react'
import {
  Formik,
} from 'formik'

import {
  FormSchema,
  FormHandlers,
  FormWrapperChildrenFactory,
  FormWrapperRenderProps,
} from '../../../types/frontend/form'

import {
  createYupSchema,
} from './utils'

import Render from './Render'

interface FormWrapperProps {
  schema?: FormSchema,
  validationSchema?: yup.ObjectSchema<any>,
  autoValidation?: boolean,
  initialValues: any,
  error?: string,
  readonly?: boolean,
  handlers?: FormHandlers,
  children: FormWrapperChildrenFactory,
  onSubmit: {
    (data: any): void,
  }
}

const FormWrapper: FC<FormWrapperProps> = ({
  validationSchema,
  schema,
  autoValidation = false,
  initialValues,
  error,
  readonly,
  handlers = {},
  children,
  onSubmit,
}) => {
  const [ showErrors, setShowErrors ] = useState(false)

  const useValidationSchema = useMemo(() => {
    if(validationSchema) return validationSchema
    if(!autoValidation || !schema) return null
    return createYupSchema(schema)
  }, [
    schema,
    validationSchema,
    autoValidation,
  ])

  return (
    <Formik
      initialValues={ initialValues }
      validationSchema={ useValidationSchema }
      validateOnMount
      validate={ handlers.validate }
      onSubmit={ onSubmit }
    >
      {
        ({
          handleSubmit,
          isValid,
          values,
          errors,
          touched,
          setFieldValue,
        }) => {
          const submitWrapper = () => {
            setShowErrors(true)
            handleSubmit()
          }

          const render = (schema: FormSchema, options?: FormWrapperRenderProps): ReactNode => {
            return (
              <Render
                fullHeight
                schema={ schema }
                handlers={ handlers }
                error={ error }
                spacing={ typeof(options?.spacing) === 'number' ? options.spacing : 2 }
                readonly={ readonly }
                isValid={ isValid }
                values={ values }
                errors={ errors }
                touched={ touched }
                showErrors={ showErrors }
                hideBottomError={ options ? options.hideBottomError : false }
              />
            )
          }

          return children({
            isValid,
            values,
            errors,
            showErrors,
            touched,
            render,
            setFieldValue,
            onSubmit: submitWrapper,
          })
        }
      }
    </Formik>
  )
}

export default FormWrapper